import {
  notAsked,
  pointerFor,
  type FormContext,
  type FormElement,
  type FormProblem,
  type JsonValue,
  type NotAsked,
  type PublishedInstrumentSchema,
  type SubmissionDocumentUnbranded,
} from "@thalamos/haven-common";

export type ItemElement = Extract<FormElement, { kind: "item" }>;

/**
 * One question as a screen answers it: its response, and any comment on it.
 * The document holds the comment as an answer of its own, where the element
 * says, so the two are read and written together.
 */
export type QuestionAnswer = { response: JsonValue; comment?: string };

/** A radio's value is a string, so an option is known by its JSON spelling, which keeps `2` and `"2"` apart. */
export const radioValueOf = (value: JsonValue) => JSON.stringify(value);

export const answerOf = (
  document: SubmissionDocumentUnbranded,
  element: ItemElement,
): QuestionAnswer | undefined => {
  const answer = document[element.at.group]?.[element.at.item];
  const { commentAt } = element;
  const comment =
    commentAt === undefined
      ? undefined
      : document[commentAt.group]?.[commentAt.item]?.response;

  return answer === undefined
    ? undefined
    : {
        response: answer.response,
        ...(typeof comment === "string" ? { comment } : {}),
      };
};

/**
 * The document with an empty group for each the instrument requires and it
 * lacks, since a required group of optional items is answered by being there.
 */
export const withRequiredGroups = (
  schema: PublishedInstrumentSchema,
  document: SubmissionDocumentUnbranded,
): SubmissionDocumentUnbranded => ({
  ...Object.fromEntries((schema.required ?? []).map((group) => [group, {}])),
  ...document,
});

/**
 * The document with one question's answer replaced, or removed where `answer`
 * is `undefined`. A comment is about its item's answer, so it goes with it,
 * and an empty one is left out.
 */
export const withAnswer = (
  schema: PublishedInstrumentSchema,
  document: SubmissionDocumentUnbranded,
  element: ItemElement,
  answer: QuestionAnswer | undefined,
): SubmissionDocumentUnbranded => {
  const { commentAt } = element;
  const others = Object.entries(document[element.at.group] ?? {}).filter(
    ([item]) => item !== element.at.item && item !== commentAt?.item,
  );
  const given =
    answer === undefined
      ? []
      : [
          [element.at.item, { response: answer.response }] as const,
          ...(commentAt === undefined ||
          answer.comment === undefined ||
          answer.comment === ""
            ? []
            : [[commentAt.item, { response: answer.comment }] as const]),
        ];
  const group = Object.fromEntries([...others, ...given]);
  const otherGroups = Object.entries(document).filter(
    ([name]) => name !== element.at.group,
  );

  // An optional group left with no answers is dropped, since one present but
  // empty would hold the form to the items it insists on there.
  return Object.fromEntries(
    Object.keys(group).length === 0 &&
      !(schema.required ?? []).includes(element.at.group)
      ? otherGroups
      : [...otherGroups, [element.at.group, group]],
  );
};

/** The groups and items the form does not ask, given the document's answers so far. */
export const hiddenIn = (
  schema: PublishedInstrumentSchema,
  form: readonly FormElement[],
  document: SubmissionDocumentUnbranded,
  context: FormContext,
): NotAsked =>
  // Errs only for a schema no validator can be built from; meanwhile nothing
  // is hidden.
  notAsked({ schema, form, document, context }).unwrapOr({
    groups: new Set(),
    items: new Set(),
  });

/**
 * The document with every hidden item's answer dropped, repeated until nothing
 * more hides, since dropping one answer can hide another item that read it.
 */
export const withoutHidden = (
  schema: PublishedInstrumentSchema,
  form: readonly FormElement[],
  document: SubmissionDocumentUnbranded,
  context: FormContext,
): SubmissionDocumentUnbranded => {
  const hidden = hiddenIn(schema, form, document, context);
  const next = form
    .filter(
      (element): element is ItemElement =>
        element.kind === "item" &&
        hidden.items.has(element.at.item) &&
        answerOf(document, element) !== undefined,
    )
    .reduce(
      (current, element) => withAnswer(schema, current, element, undefined),
      document,
    );

  return next === document
    ? document
    : withoutHidden(schema, form, next, context);
};

/**
 * The document once one item is answered as given, with the instrument's
 * required groups present and the answers of anything the change hides
 * dropped: what a screen answering one item saves.
 */
export const answered = (
  {
    schema,
    form,
    context,
  }: {
    schema: PublishedInstrumentSchema;
    form: readonly FormElement[];
    context: FormContext;
  },
  document: SubmissionDocumentUnbranded,
  element: ItemElement,
  answer: QuestionAnswer | undefined,
): SubmissionDocumentUnbranded =>
  withoutHidden(
    schema,
    form,
    withAnswer(schema, withRequiredGroups(schema, document), element, answer),
    context,
  );

/**
 * A domain a change to the answers brought into the form or took out of it.
 * A removed domain's answers go with it, so it says how many there were.
 */
export type DomainChange =
  | { kind: "added"; title: string }
  | { kind: "removed"; title: string; answersDropped: number };

/** The domains `after` asks that `before` did not, and those it no longer asks, in the form's order. */
export const domainChangesBetween = (
  {
    schema,
    form,
    context,
  }: {
    schema: PublishedInstrumentSchema;
    form: readonly FormElement[];
    context: FormContext;
  },
  {
    before,
    after,
  }: {
    before: SubmissionDocumentUnbranded;
    after: SubmissionDocumentUnbranded;
  },
): readonly DomainChange[] => {
  const hiddenBefore = hiddenIn(schema, form, before, context).groups;
  const hiddenAfter = hiddenIn(schema, form, after, context).groups;

  return form.flatMap((element): DomainChange[] => {
    if (element.kind !== "group") {
      return [];
    }

    const { group } = element.at;
    const title = element.presentation.title.text;

    if (hiddenBefore.has(group) && !hiddenAfter.has(group)) {
      return [{ kind: "added", title }];
    }

    if (!hiddenBefore.has(group) && hiddenAfter.has(group)) {
      const answersDropped = form.filter(
        (candidate) =>
          candidate.kind === "item" &&
          candidate.at.group === group &&
          answerOf(before, candidate) !== undefined,
      ).length;

      return [{ kind: "removed", title, answersDropped }];
    }

    return [];
  });
};

/** The problems reported at an answer, or anywhere inside it. */
const problemsWithin = (
  problems: readonly FormProblem[],
  { group, item }: { group: string; item: string },
): FormProblem[] => {
  const at = pointerFor([group, item]);

  return problems.filter(
    (problem) => problem.at === at || problem.at.startsWith(`${at}/`),
  );
};

/** The problems reported at an item's answer, leaving out its comment's. */
export const problemsAt = (
  problems: readonly FormProblem[],
  element: ItemElement,
): FormProblem[] => problemsWithin(problems, element.at);

/** The problems reported at an item's comment. */
export const commentProblemsAt = (
  problems: readonly FormProblem[],
  element: ItemElement,
): FormProblem[] =>
  element.commentAt === undefined
    ? []
    : problemsWithin(problems, element.commentAt);
